import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { CertificateCreateDrawer } from "../components/certificates/CertificateCreateDrawer";
import { Icon } from "../components/ui/Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

interface CertRow {
  id: string;
  code: string;
  name: string;
  certType: string;
  scope: string;
  issuer: string | null;
  issuedAt: string | null;
  validUntil: string | null;
  isActive: boolean;
}

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function validWarn(validUntil: string | null): "ok" | "soon" | "past" {
  if (!validUntil) return "ok";
  const days = (new Date(validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (days < 0) return "past";
  if (days < 60) return "soon";
  return "ok";
}
const SCOPE_LABEL: Record<string, string> = { company: "Firma", item: "Ürün", supplier: "Tedarikçi", lot: "Lot" };

export function CertificatesPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [createOpen, setCreateOpen] = useState(false);

  const input = useMemo(() => ({ page, pageSize, q: q.trim() || undefined }), [page, q]);
  const certsQ = trpc.certificate.list.useQuery(input);

  const rows = (certsQ.data?.rows ?? []) as unknown as CertRow[];
  const total = certsQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={15} />Yeni Sertifika</button>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="Sertifika adı, tür ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
        </div>

        {certsQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q ? `"${q}" için sonuç yok.` : "Henüz sertifika yok"}</div>
        ) : (
          <>
            {rows.map((c) => {
              const warn = validWarn(c.validUntil);
              return (
                <MCard pad key={c.id}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{c.name}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{c.code}</span>
                    </span>
                    <span className="badge b-neutral no-dot">{c.certType}</span>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div className="m-ovl">Kapsam</div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{SCOPE_LABEL[c.scope] ?? c.scope}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="m-ovl">Veren</div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.issuer ?? "—"}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="m-ovl">Geçerlilik</div>
                      <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2, color: warn === "past" ? "var(--danger)" : warn === "soon" ? "var(--warning)" : "var(--neutral-800)" }}>{fmtDate(c.validUntil)}</div>
                    </div>
                  </div>
                  {warn === "past" ? (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 11, fontWeight: 600, color: "var(--danger)" }}><Icon name="alertTriangle" size={13} />Geçerlilik doldu</div>
                  ) : warn === "soon" ? (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 11, fontWeight: 600, color: "var(--warning)" }}><Icon name="clock" size={13} />Geçerlilik yaklaşıyor</div>
                  ) : null}
                </MCard>
              );
            })}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        <CertificateCreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Sertifikalar</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Sertifikalar</h1>
          <p className="sub">HACCP · ISO 22000 · Helal · CoA · geçerlilik takibi</p>
        </div>
        <button type="button" className="btn-md primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Yeni Sertifika</button>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Sertifika adı, tür ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => certsQ.refetch()} disabled={certsQ.isFetching}>{certsQ.isFetching ? "Yükleniyor…" : "Yenile"}</button>
        </div>

        <div className="dt2-scroll">
          {certsQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Sertifika yok</p>
              <p className="sub">{q ? `"${q}" için sonuç yok.` : "İlk sertifikayı ekleyin."}</p>
              {!q ? <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} />Yeni Sertifika</button> : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Kod</th>
                  <th>Ad</th>
                  <th style={{ width: 100 }}>Tür</th>
                  <th style={{ width: 90 }}>Kapsam</th>
                  <th style={{ width: 140 }}>Veren</th>
                  <th style={{ width: 120 }}>Geçerlilik</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const warn = validWarn(c.validUntil);
                  return (
                    <tr key={c.id}>
                      <td><span className="cell-id">{c.code}</span></td>
                      <td>{c.name}</td>
                      <td><span className="badge-pill">{c.certType}</span></td>
                      <td style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{SCOPE_LABEL[c.scope] ?? c.scope}</td>
                      <td style={{ fontSize: 12, color: "var(--neutral-700)" }}>{c.issuer ?? "—"}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span className="mono" style={{ fontSize: 11.5, color: warn === "past" ? "var(--danger)" : warn === "soon" ? "var(--warning)" : "var(--neutral-600)" }}>
                            {fmtDate(c.validUntil)}
                          </span>
                          {warn === "past" ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 600, color: "var(--danger)" }}><Icon name="alertTriangle" size={12} />Doldu</span>
                          ) : warn === "soon" ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 600, color: "var(--warning)" }}><Icon name="clock" size={12} />Yaklaşıyor</span>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{total}</b> sertifika</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      <CertificateCreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
